import { Bell, Check, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotifications } from '@/hooks/useNotifications';
import { useLocalizedNavigate } from '@/components/i18n';
import { cn } from '@/lib/utils';
import { useState } from 'react';

function timeAgo(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  return date.toLocaleDateString();
}

const TYPE_STYLES: Record<string, string> = {
  tip: 'bg-amber-100 text-amber-700',
  milestone: 'bg-green-100 text-green-700',
  feature: 'bg-blue-100 text-blue-700',
  system: 'bg-slate-100 text-slate-700',
};

export function NotificationBell() {
  const { notifications, unreadCount, isLoading, markAsRead, markAllRead } =
    useNotifications();
  const navigate = useLocalizedNavigate();
  const [open, setOpen] = useState(false);

  const handleNotificationClick = (notification: {
    id: string;
    read_at: string | null;
    cta_url: string | null;
  }) => {
    if (!notification.read_at) {
      markAsRead(notification.id);
    }
    if (notification.cta_url) {
      setOpen(false);
      navigate(notification.cta_url);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative h-9 w-9 p-0"
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        >
          <Bell className="h-5 w-5 text-slate-500" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              <Check className="h-3 w-3" />
              Mark all read
            </button>
          )}
        </div>

        {/* Notification list */}
        <ScrollArea className="max-h-[360px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-primary" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Bell className="mb-2 h-8 w-8 text-slate-300" />
              <p className="text-sm font-medium text-slate-500">
                You're all caught up!
              </p>
              <p className="mt-1 text-xs text-slate-400">
                No notifications right now.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {notifications.map((notification) => (
                <li key={notification.id}>
                  <button
                    onClick={() => handleNotificationClick(notification)}
                    className={cn(
                      'flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50',
                      !notification.read_at && 'bg-primary/[0.03]',
                    )}
                  >
                    {/* Unread indicator */}
                    <div className="mt-1.5 flex-shrink-0">
                      {!notification.read_at ? (
                        <div className="h-2 w-2 rounded-full bg-primary" />
                      ) : (
                        <div className="h-2 w-2" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={cn(
                            'text-sm leading-tight',
                            !notification.read_at
                              ? 'font-semibold text-slate-900'
                              : 'font-medium text-slate-700',
                          )}
                        >
                          {notification.title}
                        </p>
                        <span
                          className={cn(
                            'flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                            TYPE_STYLES[notification.type] ?? TYPE_STYLES.system,
                          )}
                        >
                          {notification.type}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                        {notification.body}
                      </p>
                      <div className="mt-1.5 flex items-center gap-2">
                        <span className="text-[11px] text-slate-400">
                          {timeAgo(notification.created_at)}
                        </span>
                        {notification.cta_url && notification.cta_label && (
                          <span className="flex items-center gap-0.5 text-[11px] font-medium text-primary">
                            {notification.cta_label}
                            <ExternalLink className="h-2.5 w-2.5" />
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

export default NotificationBell;
